import { PERMISSION_LABELS, PLATFORM_ROLE_LABELS, type Permission } from '@azko/shared';
import { api } from '../../lib/api.js';
import { useSession } from '../../lib/session.js';
import { Button, Card, StatusBadge } from '../../components/ui.js';
import { TwoFactorCard } from '../../components/TwoFactorCard.js';
import { IconShield } from '../../components/icons.js';

/**
 * Configuracoes da conta de equipe.
 *
 * A lista de permissoes morava na visao geral, e era a maior coisa da tela:
 * quem abria o backoffice para responder "tem alguem esperando por mim?"
 * recebia primeiro um inventario do proprio cargo. Isso e informacao de
 * conferencia — consultada quando algo da 403, nao a cada manha —, e por isso
 * mudou para ca.
 *
 * A lista continua vindo da API, e nao de uma copia no front: e exatamente a
 * mesma que o `authorize()` usa para autorizar de verdade. Se a tela mostrasse
 * uma copia local, ela mentiria no dia em que os papeis mudassem no servidor.
 *
 * Somente leitura, e de proposito: conceder permissao a si mesmo nao e
 * configuracao, e escalonamento de privilegio. Quem pode mexer em papeis tem
 * `plataforma:papeis:gerenciar` e uma tela propria (ainda por construir).
 */

/** Nome amigavel do dominio — o segmento do meio de `plataforma:logs:ver`. */
const AREA_LABELS: Record<string, string> = {
  assinantes: 'Assinantes',
  planos: 'Planos',
  promocoes: 'Promocoes',
  logs: 'Auditoria',
  papeis: 'Papeis e permissoes',
  estrutura: 'Blocos e unidades',
  assinatura: 'Assinatura',
  faturas: 'Faturas',
  porteiros: 'Porteiros',
  moradores: 'Moradores',
  unidades: 'Unidades',
  encomendas: 'Encomendas',
};

/**
 * Agrupa por area. As permissoes tem duas formas — `dominio:area:acao` e
 * `area:acao` (o modulo de encomendas nao tem dominio) — entao a area e o
 * penultimo segmento nas duas.
 */
function areaOf(permission: Permission): string {
  const parts = permission.split(':');
  const key = parts[parts.length - 2] ?? parts[0] ?? '';
  return AREA_LABELS[key] ?? key;
}

export function AdminSettingsPage() {
  const { user, staff } = useSession();
  if (!user || !staff) return null;

  const permissions = [...staff.permissions].sort();

  const areas = new Map<string, Permission[]>();
  for (const permission of permissions) {
    const area = areaOf(permission);
    areas.set(area, [...(areas.get(area) ?? []), permission]);
  }

  async function signOut() {
    try {
      await api.post('/auth/logout');
    } finally {
      window.location.reload();
    }
  }

  return (
    <>
      <div>
        <p className="eyebrow">Equipe Azko</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Configuracoes.</h1>
        <p className="mt-2 text-sm text-muted">Sua conta na plataforma e o que ela alcanca.</p>
      </div>

      {/* Duas colunas: a conta e curta e fixa, as permissoes crescem. Empilhar
          as duas deixaria meia tela vazia ao lado de uma lista de 17 linhas. */}
      <div className="grid items-start gap-4 lg:grid-cols-[22rem_minmax(0,1fr)]">
        <div className="space-y-4">
          {/* Antes das permissoes: a conta que alcanca todos os condominios e
              exatamente a que mais precisa do segundo fator, e um cartao no fim
              de uma lista de 17 permissoes nao seria visto. */}
          <TwoFactorCard />

          <Card className="p-5 sm:p-6">
            <p className="eyebrow">Conta</p>

            <div className="mt-4 flex items-center gap-3">
              <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-surface-2 text-sm font-bold">
                {user.initials}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-lg font-bold tracking-tight">{user.name}</span>
                <span className="block truncate text-sm text-muted">{user.email}</span>
              </span>
            </div>

            <dl className="mt-5 space-y-3 border-t border-line-soft pt-5 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <dt className="text-muted">Papeis</dt>
                <dd className="font-semibold">
                  {staff.roles.map((role) => PLATFORM_ROLE_LABELS[role]).join(', ')}
                </dd>
              </div>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <dt className="text-muted">Escopo</dt>
                <dd className="font-semibold">Toda a plataforma</dd>
              </div>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <dt className="text-muted">Permissoes</dt>
                <dd className="font-semibold tabular-nums">{permissions.length}</dd>
              </div>
            </dl>
          </Card>

          <Card className="p-5 sm:p-6">
            <p className="eyebrow">Sessao</p>
            <p className="mt-2 text-sm text-muted">
              Sair encerra a sessao neste navegador. Toda acao da equipe fica no log permanente da
              conta em que foi feita — inclusive depois de sair.
            </p>
            <Button variant="ghost" className="mt-4" onClick={() => void signOut()}>
              Sair da conta
            </Button>
          </Card>
        </div>

        <Card className="p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="eyebrow">Permissoes</p>
              <h2 className="mt-2 flex items-center gap-2 text-xl font-bold tracking-tight">
                <IconShield className="size-5 text-accent" />
                {permissions.length} liberadas
              </h2>
            </div>
            <StatusBadge tone="neutral">somente leitura</StatusBadge>
          </div>

          <p className="mt-3 max-w-2xl text-sm text-muted">
            Vem dos papeis atribuidos a voce, e sao a mesma lista que a API usa para autorizar. Para
            mudar, fale com um administrador Azko.
          </p>

          <div className="mt-6 grid gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
            {[...areas].map(([area, list]) => (
              <section key={area}>
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-faint">
                  {area}
                </p>
                <ul className="mt-2 space-y-2">
                  {list.map((permission) => (
                    <li key={permission}>
                      <span className="block text-sm font-semibold">
                        {PERMISSION_LABELS[permission]}
                      </span>
                      <code className="block text-xs text-faint">{permission}</code>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
